import React, { createContext, useContext } from "react";
import { useAuth0 } from "../auth/react-auth0-spa";
import userApiConfig from "../config/userApiConfig";
import {
  invokeApiOperation,
  cloneDateTimeAndAppendSecondsOffset,
  encodeUriString,
} from "./common/ApiHelper";

const UserApiContext = createContext();
const HttpMethod = {
  Post: "POST",
  Get: "GET",
  Delete: "DELETE",
  Put: "PUT",
};
const MaxPageableUsers = 1000;
const MaxPageableUserLogs = 1000;

const getSanitizedUserId = (userId) => {
  return encodeUriString(userId);
};

export const UserApiProvider = (props) => {
  const { getTokenSilently } = useAuth0();

  const regenerateMfaRecoveryCode = async (userId, languageCode, comment) => {
    return await invokeApi(
      `users/${getSanitizedUserId(userId)}/regenerateMfaRecoveryCode`,
      HttpMethod.Post,
      false,
      {
        languageCode,
        comment,
      }
    );
  };

  const fetchUsers = async (searchBy, searchValue, skip = 0, take = 15) => {
    if (skip > MaxPageableUsers)
      throw new Error("Max pageable user count exceeded");

    const sanitizedTake = Math.min(MaxPageableUsers - skip, take);
    let urlSuffix = `users?skip=${skip}&take=${sanitizedTake}`;
    if (
      searchBy &&
      searchBy.length > 0 &&
      searchValue &&
      searchValue.length >= userApiConfig.minSearchTermLength
    ) {
      const sanitizedSearchValue = encodeUriString(searchValue);
      urlSuffix += `&${searchBy}=${sanitizedSearchValue}`;
    }
    return await invokeApi(urlSuffix, HttpMethod.Get, true);
  };

  const fetchUser = async (userId) => {
    return await invokeApi(
      `users/${getSanitizedUserId(userId)}`,
      HttpMethod.Get,
      true
    );
  };

  const fetchDeviceCredentials = async (userId) => {
    return await invokeApi(
      `users/${getSanitizedUserId(userId)}/devicecredentials`,
      HttpMethod.Get,
      true
    );
  };

  const deleteDeviceCredential = async (
    userId,
    deviceCredentialId,
    languageCode,
    comment
  ) => {
    return await invokeApi(
      `users/${getSanitizedUserId(
        userId
      )}/deviceCredentials/${deviceCredentialId}`,
      HttpMethod.Delete,
      false,
      {
        languageCode,
        comment,
      }
    );
  };

  const fetchMfaEnrollments = async (userId) => {
    return await invokeApi(
      `users/${getSanitizedUserId(userId)}/mfaenrollments`,
      HttpMethod.Get,
      true
    );
  };

  const deleteMfaEnrollment = async (
    userId,
    mfaEnrollmentId,
    verificationCode,
    languageCode,
    comment
  ) => {
    return await invokeApi(
      `users/${getSanitizedUserId(userId)}/mfaenrollments/${mfaEnrollmentId}`,
      HttpMethod.Delete,
      false,
      {
        verificationCode,
        languageCode,
        comment,
      }
    );
  };

  const deleteAllMfaEnrollments = async (
    userId,
    verificationCode,
    languageCode,
    comment
  ) => {
    return await invokeApi(
      `users/${getSanitizedUserId(userId)}/mfaenrollments`,
      HttpMethod.Delete,
      false,
      {
        verificationCode,
        languageCode,
        comment,
      }
    );
  };

  const fetchUserLogs = async (
    userId,
    dateTimeAfterUtc,
    dateTimeBeforeUtc,
    skip = 0,
    take = 15
  ) => {
    if (skip > MaxPageableUserLogs)
      throw new Error("Max pageable count exceeded");

    const sanitizedTake = Math.min(MaxPageableUserLogs - skip, take);
    const sanitizedAfterParam = getSanitizedDateParam(
      "dateTimeAfterUtc",
      cloneDateTimeAndAppendSecondsOffset(dateTimeAfterUtc, -1)
    );
    const sanitizedBeforeParam = getSanitizedDateParam(
      "dateTimeBeforeUtc",
      cloneDateTimeAndAppendSecondsOffset(dateTimeBeforeUtc, 1)
    );

    const sanitizedUserId = getSanitizedUserId(userId);
    const url = `users/${sanitizedUserId}/logs?skip=${skip}&take=${sanitizedTake}${sanitizedAfterParam}${sanitizedBeforeParam}`;
    return await invokeApi(url, HttpMethod.Get, true);
  };

  const getSanitizedDateParam = (paramName, date) => {
    return `&${paramName}=${date.toJSON()}`;
  };

  const blockUser = async (userId, languageCode, comment) => {
    return await invokeApi(
      `users/${getSanitizedUserId(userId)}/block`,
      HttpMethod.Put,
      true,
      {
        languageCode,
        comment,
      }
    );
  };

  const unblockUser = async (userId, languageCode, comment) => {
    return await invokeApi(
      `users/${getSanitizedUserId(userId)}/unblock`,
      HttpMethod.Put,
      true,
      {
        languageCode,
        comment,
      }
    );
  };

  const deleteUser = async (userId, languageCode, comment) => {
    return await invokeApi(
      `users/${getSanitizedUserId(userId)}`,
      HttpMethod.Delete,
      false,
      {
        languageCode,
        comment,
      }
    );
  };

  const startChangeEmail = async (userId, email, languageCode, comment) => {
    return await invokeApi(
      `users/${getSanitizedUserId(userId)}/startChangeEmail`,
      HttpMethod.Post,
      false,
      {
        newEmail: email,
        languageCode,
        comment,
      }
    );
  };

  const completeChangeEmail = async (userId, verificationCode) => {
    return await invokeApi(
      `users/${getSanitizedUserId(userId)}/completeChangeEmail`,
      HttpMethod.Post,
      true,
      {
        verificationCode,
      }
    );
  };

  const sendEmailAccessVerificationCode = async (
    userId,
    languageCode,
    comment
  ) => {
    return await invokeApi(
      `users/${getSanitizedUserId(userId)}/sendVerificationEmail`,
      HttpMethod.Post,
      false,
      {
        languageCode,
        comment,
      }
    );
  };

  const validateEmailAccessVerificationCode = async (
    userId,
    verificationCode
  ) => {
    return await invokeApi(
      "users/validateEmailVerificationCode",
      HttpMethod.Post,
      true,
      {
        id: userId,
        verificationCode,
      }
    );
  };

  const resetPassword = async (userId, languageCode, comment) => {
    return await invokeApi(
      `users/${getSanitizedUserId(userId)}/resetPassword`,
      HttpMethod.Post,
      true,
      {
        languageCode,
        comment,
      }
    );
  };

  const bypassEmailVerification = async (
    userId,
    newEmail,
    languageCode,
    comment
  ) => {
    return await invokeApi(
      `users/${getSanitizedUserId(userId)}/bypassEmailVerification`,
      HttpMethod.Post,
      true,
      {
        email: newEmail,
        languageCode,
        comment,
      }
    );
  };

  const bypassMfa = async (
    userId,
    comment,
    bypassEnabled
  ) => {
    return await invokeApi(
      `users/${getSanitizedUserId(userId)}/setMfaBypass`,
      HttpMethod.Post,
      true,
      {
        comment,
        BypassEnabled: bypassEnabled
      }
    );
  };

  const invokeApi = async (
    urlSuffix,
    httpMethod,
    parseResponseToJson,
    bodyData = null
  ) => {
    const token = await getTokenSilently();
    return await invokeApiOperation(
      token,
      urlSuffix,
      httpMethod,
      parseResponseToJson,
      bodyData
    );
  };

  const value = {
    regenerateMfaRecoveryCode:
      props.regenerateMfaRecoveryCode || regenerateMfaRecoveryCode,
    fetchUsers: props.fetchUsers || fetchUsers,
    fetchUser: props.fetchUser || fetchUser,
    fetchDeviceCredentials:
      props.fetchDeviceCredentials || fetchDeviceCredentials,
    deleteDeviceCredential:
      props.deleteDeviceCredential || deleteDeviceCredential,
    fetchMfaEnrollments: props.fetchMfaEnrollments || fetchMfaEnrollments,
    deleteMfaEnrollment: props.deleteMfaEnrollment || deleteMfaEnrollment,
    deleteAllMfaEnrollments:
      props.deleteAllMfaEnrollments || deleteAllMfaEnrollments,
    fetchUserLogs: props.fetchUserLogs || fetchUserLogs,
    blockUser: props.blockUser || blockUser,
    unblockUser: props.unblockUser || unblockUser,
    deleteUser: props.deleteUser || deleteUser,
    startChangeEmail: props.startChangeEmail || startChangeEmail,
    completeChangeEmail: props.completeChangeEmail || completeChangeEmail,
    sendEmailAccessVerificationCode:
      props.sendEmailAccessVerificationCode || sendEmailAccessVerificationCode,
    validateEmailAccessVerificationCode:
      props.validateEmailAccessVerificationCode ||
      validateEmailAccessVerificationCode,
    resetPassword: props.resetPassword || resetPassword,
    bypassEmailVerification:
      props.bypassEmailVerification || bypassEmailVerification,
    bypassMfa:
      props.bypassMfa || bypassMfa,
  };

  return (
    <UserApiContext.Provider value={value}>
      {props.children}
    </UserApiContext.Provider>
  );
};

export const useUserApi = () => {
  return useContext(UserApiContext);
};
